import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import RefreshIcon from '../../../assets/icons/refresh.svg?react';
import SmsHistoryItem from './SmsHistoryItem';
import Pagination from '../../../components/Pagination/Pagination';
import { SkeletonTable } from '../../../components/Skeleton';
import { useSmsHistory } from '../../../hooks/queries/smsQueries';
import Calendar from '../../../components/Calendar';
import dayjs from 'dayjs';

const SmsHistory = () => {
  const [detailModalOn, setDetailModalOn] = useState(false);
  const [target, setTarget] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshMsg, setRefreshMsg] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 10;
  const sortBy = searchParams.get('sortBy') || 'SentDate';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  const keyword = searchParams.get('Keyword') || '';
  const fromDateParam = searchParams.get('fromDate') || '';
  const toDateParam = searchParams.get('toDate') || '';

  // URL에서 날짜 범위 읽기 (없으면 기본값: 1주일 전부터 오늘까지)
  const getInitialDateRange = () => {
    if (fromDateParam && toDateParam) {
      const startDate = dayjs(fromDateParam, 'YYYY-MM-DD');
      const endDate = dayjs(toDateParam, 'YYYY-MM-DD');
      if (startDate.isValid() && endDate.isValid()) {
        return [startDate, endDate];
      }
    }
    // 기본값: 1주일 전부터 오늘까지
    return [dayjs().subtract(1, 'week'), dayjs()];
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);
  
  // keywordInput을 URL의 keyword와 동기화
  useEffect(() => {
    if (keyword) {
      setKeywordInput(keyword);
    } else {
      setKeywordInput('');
    }
  }, [keyword]);

  // URL 파라미터가 변경되면 dateRange 업데이트
  useEffect(() => {
    const newDateRange = getInitialDateRange();
    setDateRange(newDateRange);
  }, [fromDateParam, toDateParam]);
  
  // 날짜 범위 변경 핸들러
  const handleDateRangeChange = (newDateRange) => {
    if (newDateRange && Array.isArray(newDateRange) && newDateRange.length === 2) {
      setDateRange(newDateRange);
      // URL 파라미터 업데이트
      const current = Object.fromEntries(searchParams.entries());
      setSearchParams({
        ...current,
        fromDate: dayjs(newDateRange[0]).format('YYYY-MM-DD'),
        toDate: dayjs(newDateRange[1]).format('YYYY-MM-DD'),
        page: '1' // 날짜 변경 시 첫 페이지로
      });
    }
  };

  // API 호출용 날짜 형식
  const fromDate = useMemo(() => dayjs(dateRange[0]).format('YYYY-MM-DD'), [dateRange]);
  const toDate = useMemo(() => dayjs(dateRange[1]).format('YYYY-MM-DD'), [dateRange]);

  // Query Params Memoization
  const queryParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
      OrderBy: sortBy,
      OrderDirection: sortOrder
    };
    
    if (keyword) {
      params.Keyword = keyword;
    }
    
    if (fromDate) {
      params.FromDate = fromDate;
    }
    
    if (toDate) {
      params.ToDate = toDate;
    }
    
    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, keyword, fromDate, toDate]);
  
  const { data: history, isLoading, error, refetch } = useSmsHistory(queryParams);
  
  // 키워드 입력 핸들러
  const handleKeywordChange = (e) => {
    setKeywordInput(e.target.value);
  };
  
  // 키워드 검색 핸들러 (Enter 키)
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      const value = e.target.value.trim();
      if (value.length >= 2) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('Keyword', value);
          newParams.set('page', '1');
          return newParams;
        });
      } else if (value.length === 0) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('Keyword');
          newParams.set('page', '1');
          return newParams;
        });
      }
    }
  };

  const handlePageChange = (page) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  const totalPages = history?.Total ? Math.ceil(history.Total / pageSize) : 1;

  const handleViewDetail = (sms) => {
    setTarget(sms);
    setDetailModalOn(true);
  };

  const handleRefresh = async () => {
    try {
      setRefreshMsg('...');
      await refetch();
      setRefreshMsg('발송 이력 새로고침 완료!');
      setTimeout(() => {
        setRefreshMsg('');
      }, 1500);
    } catch (error) {
      console.error('발송 이력 새로고침 실패:', error);
      setRefreshMsg('발송 이력 새로고침 실패!');
      setTimeout(() => {
        setRefreshMsg('');
      }, 1500);
    }
  };

  return (
    <>
      <div className="flex justify-between align-center mb-10">
        <div className="flex w-half gap-10">
          <div className="calendar-filters">
            <Calendar
              mode="range"
              value={dateRange}
              onChange={handleDateRangeChange}
            />
          </div>
          <input 
            type="text" 
            placeholder="키워드 검색 (2자 이상) - 이름, 연락처" 
            className="input-primary flex-1 text-b2-400" 
            value={keywordInput}
            onChange={handleKeywordChange}
            onKeyDown={handleKeywordKeyDown}
          />
        </div>
        <div className="flex justify-end align-center gap-5">
          <p className="text-b2-400 text-primary">{refreshMsg}</p>
          <button className="btn big-btn btn-style-5 flex align-center gap-3" onClick={handleRefresh}>
            <RefreshIcon color='var(--gray-600)'/>
            <p className="text-b2-400 text-gray-600">새로고침</p>
          </button>
        </div>
      </div>
      {isLoading ? (
        <SkeletonTable rows={5} cols={7} />
      ) : error ? (
        <div className="error-message">데이터를 불러오는데 실패했습니다</div>
      ) : (
        <div className="primary-table">
          <table>
            <thead>
              <tr>
                <th width="50px"></th>
                <th>발송일시</th>
                <th className='sms-count' width="100px">전체 수신자</th>
                <th className='sms-count' width="100px">성공 수</th>
                <th className='sms-count' width="100px">실패 수</th>
              </tr>
            </thead>
            <tbody>
              {history?.Items?.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">
                    <p className="text-b3-700 text-gray-500">발송 이력이 없습니다.</p>
                  </td>
                </tr>
              ) : (
                history?.Items?.map(sms => (
                  <SmsHistoryItem 
                    key={sms.SmsSysId} 
                    sms={sms} 
                    onViewDetail={handleViewDetail}
                  />
                ))
              )}
            </tbody>
          </table>
          <div className="pagination-wrapper">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SmsHistory;
